// Google / GitHub sign-in (CYBORG-1262). Contract: relay /api/auth/oauth/*.
// PKCE: the verifier never leaves this browser profile; the relay only sees its
// S256 challenge, so an intercepted cyborg7:// code is useless without it.
import { z } from "zod";
import { adoptSession, wsUrlFromBase, type AdoptOutcome } from "$lib/auth/adopt-session.js";
import type { PendingDomainMatch } from "$lib/post-signup-landing.js";
import { reportUiFailure, type FailureKind } from "$lib/report-failure.js";

export type OAuthProvider = "google" | "github";

const STORE_KEY = "cyborg7.oauth.pending";
const NAMES: Record<OAuthProvider, string> = { google: "Google", github: "GitHub" };

/** How long the callback page waits for exchange + connect before it shows a failure. */
export const OAUTH_CALLBACK_TIMEOUT_MS = 20_000;

// The relay's callback `?error=` and exchange `{error}` codes. The relay logged each one.
const RELAY_REASONS = [
  "invalid_state",
  "unavailable",
  "failed",
  "access_denied",
  "email_unverified",
  "invalid_code",
  "expired",
  "account_suspended",
  "sign_in_with_account_email",
] as const;

// Failures only this client sees, so only this client can report them, and as what.
const CLIENT_REASON_KINDS = {
  missing_verifier: "refusal",
  not_in_applications: "refusal",
  timeout: "fault",
  malformed_response: "fault",
  network: "transient",
} as const satisfies Record<string, FailureKind>;

type RelayReason = (typeof RELAY_REASONS)[number];
type ClientReason = keyof typeof CLIENT_REASON_KINDS;
export type OAuthReason = RelayReason | ClientReason;

export const OAUTH_REASONS: readonly OAuthReason[] = [
  ...RELAY_REASONS,
  ...(Object.keys(CLIENT_REASON_KINDS) as ClientReason[]),
];

const RelayReasonSchema = z.enum(RELAY_REASONS);

/** A code the relay sent; one this build does not know is `failed`, never free text. */
function relayReason(raw: string): RelayReason {
  const parsed = RelayReasonSchema.safeParse(raw);
  return parsed.success ? parsed.data : "failed";
}

export class OAuthError extends Error {
  // reportUiFailure records `code`.
  readonly code: OAuthReason;

  constructor(
    readonly reason: OAuthReason,
    readonly provider?: OAuthProvider,
    options?: { cause?: unknown },
  ) {
    super(reason, options);
    this.name = "OAuthError";
    this.code = reason;
  }
}

const COPY = {
  invalid_state: () => "That sign-in link expired or was already used. Start again.",
  unavailable: (p) => `Sign-in with ${p} isn't available right now.`,
  failed: (p) => `Couldn't sign you in with ${p}. Try again or use your email.`,
  access_denied: (p) => `You cancelled sign-in with ${p}.`,
  email_unverified: (p) =>
    `Your ${p} email isn't verified. Verify it there or use your email instead.`,
  invalid_code: () => "That sign-in link expired or was already used. Start again.",
  expired: () => "That sign-in took too long to finish. Start again.",
  account_suspended: () => "This account is suspended. Contact your workspace admin.",
  sign_in_with_account_email: (p) =>
    `Your ${p} email is a secondary address on your account. Sign in with your account's main email.`,
  missing_verifier: () => "Finish sign-in in the window where you started it, or start again here.",
  not_in_applications: (provider) =>
    `Move Cyborg to Applications to sign in with ${provider}, or use your email code.`,
  timeout: (p) => `Sign-in with ${p} is taking too long. Try again.`,
  malformed_response: (p) => `Couldn't sign you in with ${p}. Try again or use your email.`,
  network: () => "Couldn't reach Cyborg. Check your connection and try again.",
} satisfies Record<OAuthReason, (provider: string) => string>;

export function oauthErrorCopy(reason: OAuthReason, provider?: OAuthProvider): string {
  return COPY[reason](provider ? NAMES[provider] : "your provider");
}

/** The reason a failure shows; any throw that is not an OAuthError shows as `failed`. */
export function oauthReasonOf(err: unknown): OAuthReason {
  return err instanceof OAuthError ? err.reason : "failed";
}

function isClientReason(reason: OAuthReason): reason is ClientReason {
  return reason in CLIENT_REASON_KINDS;
}

/**
 * How to report a failure, or undefined when the client must not: the relay already
 * logged its own codes, and `access_denied` is the person's own cancel.
 */
export function oauthFailureKind(err: unknown): FailureKind | undefined {
  if (!(err instanceof OAuthError)) return "fault";
  return isClientReason(err.reason) ? CLIENT_REASON_KINDS[err.reason] : undefined;
}

const ProvidersSchema = z.object({ providers: z.array(z.string()) });

/** A parser error quotes the start of its input: report only what failed and the error's kind. */
function opaqueFailure(what: string, err: unknown): Error {
  return new Error(`${what} (${err instanceof Error ? err.name : typeof err})`);
}

// Transport and decode fail differently: an unreachable relay is transient, a body we
// cannot read (non-JSON or the wrong shape) is a fault.
export async function fetchOAuthProviders(base: string): Promise<OAuthProvider[]> {
  let resp: Response;
  try {
    resp = await fetch(`${base}/api/auth/oauth/providers`);
  } catch (err) {
    reportUiFailure("oauth.providers", err, {}, { failureKind: "transient" });
    return [];
  }
  if (!resp.ok) {
    const err = new Error(`providers answered ${resp.status}`);
    reportUiFailure("oauth.providers", err, {}, { code: `http_${resp.status}` });
    return [];
  }
  let body: unknown;
  try {
    body = await resp.json();
  } catch (err) {
    const failure = opaqueFailure("malformed_response: non_json", err);
    reportUiFailure("oauth.providers", failure, {}, { failureKind: "fault" });
    return [];
  }
  const parsed = ProvidersSchema.safeParse(body);
  if (!parsed.success) {
    reportUiFailure("oauth.providers", parsed.error, {}, { failureKind: "fault" });
    return [];
  }
  return parsed.data.providers.filter((p): p is OAuthProvider => p in NAMES);
}

function b64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// Same-origin path with a single leading "/" — rejects "//evil.com" and "/\\evil.com".
export function safeNextPath(raw: string | null | undefined): string | null {
  return raw && raw.startsWith("/") && !raw.startsWith("//") && !raw.startsWith("/\\") ? raw : null;
}

// H10 D3: the desktop shell keeps the verifier; the web and an older shell use this tab's storage.
async function startChallenge(
  provider: OAuthProvider,
  desktop: boolean,
  next: string | null,
): Promise<string> {
  const shell = desktop ? window.cyborg7Desktop?.oauth?.begin : undefined;
  const started = shell ? await shell(provider, next) : null; // null: keep this tab's verifier
  // Audit r1: a copy that never claimed cyborg7:// would wait for a return that cannot come.
  if (started && "unavailable" in started) throw new OAuthError("not_in_applications", provider);
  if (started) return started.challenge;
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  sessionStorage.setItem(STORE_KEY, JSON.stringify({ provider, verifier, next }));
  return b64url(new Uint8Array(digest));
}

export async function beginOAuth(
  provider: OAuthProvider,
  base: string,
  desktop: boolean,
  next: string | null,
): Promise<void> {
  const challenge = await startChallenge(provider, desktop, safeNextPath(next));
  const url =
    `${base}/api/auth/oauth/${provider}/start?client=${desktop ? "desktop" : "web"}` +
    `&challenge=${challenge}`;
  // Desktop: the shell's window-open handler hands https links to the system
  // browser (Google blocks embedded webviews). Web: same-tab redirect.
  if (desktop) window.open(url, "_blank");
  else window.location.assign(url);
}

const PendingSchema = z.object({
  provider: z.enum(["google", "github"]),
  verifier: z.string().min(1),
  next: z.string().nullish(), // null: beginOAuth stores null for "no next path"
});

interface Pending {
  provider: OAuthProvider;
  verifier: string;
  next: string | null;
}

/** The verifier this tab stored at start; an unreadable record throws missing_verifier. */
function takePending(): Pending | undefined {
  let json: unknown;
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    sessionStorage.removeItem(STORE_KEY);
    if (raw === null) return undefined; // null: getItem's answer for "nothing stored"
    json = JSON.parse(raw);
  } catch (err) {
    const cause = opaqueFailure("pending record unreadable", err);
    throw new OAuthError("missing_verifier", undefined, { cause });
  }
  const parsed = PendingSchema.safeParse(json);
  if (!parsed.success) throw new OAuthError("missing_verifier", undefined, { cause: parsed.error });
  const { provider, verifier, next } = parsed.data;
  return { provider, verifier, next: safeNextPath(next) };
}

const PendingDomainMatchSchema: z.ZodType<PendingDomainMatch> = z.object({
  workspaceId: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(), // null: the relay sends null for no avatar
  memberCount: z.number(),
  members: z.array(z.object({ name: z.string(), avatarUrl: z.string().nullable() })),
  joinPolicy: z.enum(["request", "open"]),
  domain: z.string(),
});

// The same payload as login/verify. Loose: adoptSession reads these fields and the rest rides along.
const ExchangeSuccessSchema = z.looseObject({
  token: z.string().min(1),
  refreshToken: z.string().optional(),
  email: z.string().optional(),
  isNewUser: z.boolean().optional(),
  pendingDomainJoin: PendingDomainMatchSchema.nullish(), // null: no domain match
  pendingInviteToken: z.string().nullish(), // null: no pending invite
  user: z.looseObject({ email: z.string() }).optional(),
});
const ExchangeErrorSchema = z.object({ error: z.string() });

async function postExchange(
  base: string,
  code: string,
  pending: Pending,
): Promise<{ resp: Response; body: unknown }> {
  let resp: Response;
  try {
    resp = await fetch(`${base}/api/auth/oauth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, verifier: pending.verifier }),
    });
  } catch (err) {
    throw new OAuthError("network", pending.provider, { cause: err });
  }
  try {
    return { resp, body: await resp.json() };
  } catch (err) {
    const cause = opaqueFailure("malformed_response: non_json", err);
    throw new OAuthError("malformed_response", pending.provider, { cause });
  }
}

// The desktop return page fires the deep link on load AND keeps an "Open Cyborg" button,
// so the same callback can arrive twice. Module state: it outlives a login remount.
const handledCodes = new Set<string>();
let oauthSignedIn = false;

/**
 * True for a desktop callback that must be ignored: a code already handled, or an
 * error after this app signed in. Marks a new code as handled.
 */
export function isRepeatOAuthCallback(cb: {
  code?: string | null;
  error?: string | null;
}): boolean {
  if (cb.code) {
    if (handledCodes.has(cb.code)) return true;
    handledCodes.add(cb.code);
    return false;
  }
  return oauthSignedIn;
}

/** Where a desktop sign-in return goes when it wakes the app on another route (H10 D4); undefined: this page takes it. */
export function oauthReturnTarget(pathname: string): string | undefined {
  if (pathname === "/login" || pathname.startsWith("/invite/")) return undefined;
  return "/login?oauth=1";
}

/** Redeem the one-time code and adopt the session. Throws OAuthError. */
export async function completeOAuthLogin(
  base: string,
  cb: { code?: string | null; error?: string | null; pending?: unknown },
): Promise<AdoptOutcome & { email?: string }> {
  const fromShell = PendingSchema.safeParse(cb.pending);
  const pending = fromShell.success
    ? { ...fromShell.data, next: safeNextPath(fromShell.data.next) }
    : takePending();
  const provider = pending?.provider;
  if (cb.error) throw new OAuthError(relayReason(cb.error), provider);
  if (!pending || !cb.code) throw new OAuthError("missing_verifier", provider);

  const { resp, body } = await postExchange(base, cb.code, pending);
  if (!resp.ok) {
    const refusal = ExchangeErrorSchema.safeParse(body);
    if (!refusal.success) {
      throw new OAuthError("malformed_response", provider, { cause: refusal.error });
    }
    throw new OAuthError(relayReason(refusal.data.error), provider);
  }
  const parsed = ExchangeSuccessSchema.safeParse(body);
  if (!parsed.success) {
    throw new OAuthError("malformed_response", provider, { cause: parsed.error });
  }
  const data = parsed.data;
  oauthSignedIn = true;
  const outcome = await adoptSession(data, {
    wsUrl: wsUrlFromBase(base),
    nextDest: pending.next,
    isNew: data.isNewUser === true || !!data.pendingDomainJoin,
  });
  return { ...outcome, email: data.email ?? data.user?.email };
}

export interface OAuthCallbackRun {
  /** The report source, e.g. `oauth.callback`. */
  source: string;
  complete: () => Promise<AdoptOutcome & { email?: string }>;
  onOutcome: (outcome: AdoptOutcome & { email?: string }) => void | Promise<void>;
  onFailure: (err: unknown) => void;
  timeoutMs?: number;
}

/**
 * Runs the callback with a deadline and reports what the client alone saw. The deadline
 * only SHOWS a failure: a later answer still lands (a late success navigates, a late
 * error replaces the timeout copy). Returns the teardown.
 */
export function runOAuthCallback(run: OAuthCallbackRun): () => void {
  const timer = setTimeout(() => {
    const timeout = new OAuthError("timeout");
    reportUiFailure(run.source, timeout, {}, { failureKind: CLIENT_REASON_KINDS.timeout });
    run.onFailure(timeout);
  }, run.timeoutMs ?? OAUTH_CALLBACK_TIMEOUT_MS);
  void (async () => {
    try {
      const outcome = await run.complete();
      clearTimeout(timer);
      await run.onOutcome(outcome);
    } catch (err) {
      clearTimeout(timer);
      const kind = oauthFailureKind(err);
      if (kind) reportUiFailure(run.source, err, {}, { failureKind: kind });
      run.onFailure(err);
    }
  })();
  return () => clearTimeout(timer);
}
